"use client"

import React from "react";
import {Controller, useForm} from "react-hook-form";
import {valibotResolver} from "@hookform/resolvers/valibot";
import {
    CLEARANCE__COUNTED_SERVICES_DEFINITION,
    CLEARANCE__SERVICES_DEFINITION,
    ClearanceServicesSchema,
} from "@root/server/routes/web/order_wizard/[order_edit_id]/clearance/clearance.shared.ts";
import type {InferInput} from "valibot";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import {Context} from "@hotwired/stimulus";
import {RhfPillCheckboxGroup} from "@root/server/client/esweb/components/ui/rhf/RhfPillCheckboxGroup.tsx";
import {ContinueSubmitButton} from "@root/server/client/esweb/src/pages/web/shared/continue-submit-button.tsx";
import {FormServerErrors} from "@root/server/client/esweb/src/pages/web/shared/form-server-errors.tsx";
import {useFormSubmission} from "@root/server/client/esweb/src/lib/hooks/useFormSubmission.ts";
import {Input} from "@root/server/client/esweb/components/ui/input.tsx";
import {Label} from "@root/server/client/esweb/components/ui/label.tsx";
import {Field, FieldError} from "@root/server/client/esweb/components/ui/field.tsx";
import {RhfObjectKeyCheckboxGroup} from "@root/server/client/esweb/components/ui/rhf/RhfObjectKeyCheckboxGroup.tsx";
import {useLens} from "@hookform/lenses";
import {RhfSpinInput} from "@root/server/client/esweb/components/ui/rhf/RhfSpinInput.tsx";
import {
    MovingHeavyItemsField
} from "@root/server/routes/web/order_wizard/[order_edit_id]/moving/components/moving-heavy-items-field.tsx";

export const name = 'pwm--clearance_services_form';

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <ClearanceServicesFormStimulus {...props}/>);
    }
}


export type ClearanceServicesFormValues = InferInput<typeof ClearanceServicesSchema>;

interface ClearanceServicesFormProps {
    initialData?: Partial<ClearanceServicesFormValues>;
    submitUrl: string;
}

const defaultCountedServices = Object.fromEntries(
    CLEARANCE__COUNTED_SERVICES_DEFINITION.map((sd) => [sd.key, 0] as const),
) as Record<(typeof CLEARANCE__COUNTED_SERVICES_DEFINITION)[number]["key"], number>;


export function ClearanceServicesFormStimulus({initialData, submitUrl}: ClearanceServicesFormProps) {


    const {control, handleSubmit, watch} = useForm<ClearanceServicesFormValues>({
        resolver: valibotResolver(ClearanceServicesSchema),
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            heavyItems: [],
            booleanServices: {},
            countedServices: defaultCountedServices,
            ...(initialData ?? {}),
        },
    });

    const lens = useLens({control});

    const {execute, isSubmitting, serverErrors} = useFormSubmission<ClearanceServicesFormValues>({submitUrl});

    return (
        <form onSubmit={handleSubmit(execute)} className="space-y-4">

            <section className="p-4 max-sm:-mx-4">
                <div className="space-y-4">

                    {CLEARANCE__COUNTED_SERVICES_DEFINITION.map(({key, label, unit}) => (
                        <Controller
                            key={key}
                            control={control}
                            name={`countedServices.${key}` as const}
                            render={({field, fieldState}) => (
                                <Field data-invalid={fieldState.invalid}>
                                    <div  className="flex flex-wrap items-center gap-3">
                                        <Label htmlFor={`${key}-count`} className="text-sm font-medium w-full max-w-[220px]">
                                            {label}
                                        </Label>
                                        <Input
                                            id={`${key}-count`}
                                            {...field}
                                            type="number"
                                            min={0}
                                            className="w-24"
                                            onChange={(event) => field.onChange(Number.parseInt(event.target.value))}
                                        />
                                        <span className="text-sm text-muted-foreground">{unit}</span>
                                    </div>
                                    {fieldState.invalid && (
                                        <FieldError errors={[fieldState.error]} />
                                    )}
                                </Field>
                            )}
                        />
                    ))}

                    {/*{CLEARANCE__COUNTED_SERVICES_DEFINITION.map(({key, label, unit}) => (*/}
                    {/*    <RhfSpinInput {...{min: 0, max: 1000, lens: lens.focus(`countedServices.${key}`)}}/>*/}
                    {/*))}*/}
                </div>
            </section>

            <section className="p-4 max-sm:-mx-4">
                <RhfObjectKeyCheckboxGroup
                    lens={lens.focus('booleanServices')}
                    label="Služby"
                    options={CLEARANCE__SERVICES_DEFINITION}
                />
            </section>

            <section className="p-4 max-sm:-mx-4">
                <MovingHeavyItemsField control={control} name="heavyItems" />
            </section>

            <FormServerErrors errors={serverErrors} />

            <ContinueSubmitButton {...{isSubmitting}}/>

        </form>
    );
}
